/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        SPARTA Masking Utility
 * @file        sparta-masking-utility.js
 * @description Provides GIS masking logic for inputs and text values
 * @classdesc   Provides GIS masking logic for inputs and text values
 * @global      Creates a a global GISMasking object
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * TThis is a support utility for the `sparta-masking-utility` in order 
 * to pass the masking functionality between componenets.
 * 
 * The currently supported "data-field-type" attribute values are:
 *     **no value** - will apply the default mask, which will mask all characters in the field
 *     email        - the mask for email addresses, which masks only the "local" part of the
 *                    email (i.e., the part before the "@").  How the local is mask is based on
 *                    it's length:
 *                       - for email addresses with local values fewer than 3 characters, all
 *                         characters in the local are masked
 *                       - for email addresses with local values with 3 or more characters, all
 *                         characters but the first and last ones in the local are masked
 *     phone        - the mask for phone numbers, which masks the first 6 digits in the phone
 *                    number
 *     account      - the mask for Bank of America account numbers, which masks all but the last
 *                    4 digits in the field
 *     date         - the mask for dates, which masks all alphanumeric characters in the field
 *     ssn          - the mask for social security numbers (SSN) and employer identification
 *                    numbers (EIN), which masks all alphanumeric characters in the field
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

class SpartaMaskingDependencyUtility {
  get data() {
    return data;
  }
  get fn() {
    return fn;
  }
}
const spartaMaskingDependencyUtility = new SpartaMaskingDependencyUtility();
const data = {
  // data.globals - configurable "constants" used throughout the library
  globals: {
    // the base masking class
    PAGE_LANGUAGE: 'enUS',
    // the base masking class
    MASKING_CLASS_BASE: 'gis-mask',
    // the original input masking class
    MASKING_CLASS_ORIGINAL: 'gis-mask-original',
    // the ready masking class
    MASKING_CLASS_READY: 'gis-mask-value',
    // suffix used to identify associated masking fields
    MASKED_FIELD_SUFFIX: '-masked',
    // the character to be displayed in place of  any masked characters.  "\u25CF" = (the "black circle" character)
    MASKING_CHARACTER: '\u25CF',
    // the default masked delay timer
    MASKED_DELAY_TIMER: 5000,
    // suffix used to identify the "full-masking toggle" checkbox
    FULL_MASK_TOGGLE_SUFFIX: '-cbToggleFullMask',
    // prefix used to create unique input re-masking timeouts in GISMasking.data.timeOuts
    AUTO_RE_MASK_INPUT_PREFIX: 'remaskInputDelay_',
    // prefix used to create unique text re-masking timeouts in GISMasking.data.timeOuts
    AUTO_RE_MASK_TEXT_PREFIX: 'remaskTextDelay_'
  },
  // data.maskingPatterns - standard regular expressions, representing the types of characters that will be masked in a field
  maskingPatterns: {
    allChars: /./g,
    // mask all character types
    alphaNumeric: /[a-z\d]/gi // mask only alphanumeric characters
  },
  timeOuts: {}
};

// GISMasking.fn - library methods
const fn = {
  // "fn.helpers" methods - common "utility" functions that are used in multiple places in the library
  helpers: {
    /* "helpers.applyMask" method
     * "applyMask" takes a string value and creates a masked version of it, using the standard
     * masking character.  How the mask is applied is determined by patterns passed in, indicating
     * what part of the string to mask and which characters to mask, within that sub-string.
     *
     * Parameters:
     *     sValue    - a string that contains the value that needs to be masked
     *     sPattern  - a string that contains the regular expression pattern to determine what part
     *                 of "sValue" should be masked
     *     sMaskType - a string that contains the "GISMasking.data.maskingPatterns" property that
     *                 represents the group of characters should be masked, within the sub-string
     *                 determined by "sPattern"
     *
     * Returns: a string value that contains the masked version of the original "sValue" string
     */
    applyMask(sValue, sPattern, sMaskType) {
      const aPatternMatch = sValue.match(new RegExp(sPattern));

      // default the substring that should be masked to the entire string, in case the match fails
      let sMaskedPortion = sValue;

      // default the character types to match to all characters, in case the "sMaskType" value is invalid
      let regexMaskPattern = spartaMaskingDependencyUtility.data.maskingPatterns.allChars;
      if (aPatternMatch) {
        [sMaskedPortion] = aPatternMatch;
      }
      if (Object.prototype.hasOwnProperty.call(spartaMaskingDependencyUtility.data.maskingPatterns, sMaskType)) {
        regexMaskPattern = spartaMaskingDependencyUtility.data.maskingPatterns[sMaskType];
      }

      // mask the characters in the substring
      const sMaskedValue = sMaskedPortion.replace(regexMaskPattern, spartaMaskingDependencyUtility.data.globals.MASKING_CHARACTER);

      // return the original string value, with the appropriate characters masked
      return sValue.replace(sMaskedPortion, sMaskedValue);
    },
    /* "helpers.hideElement" method
     * "hideElement" changes the visibility of an element, both visibly and to screen readers.
     */
    hideElement(element, bHideElement) {
      if (bHideElement) {
        element.classList.add('spa-hidden');
      } else {
        element.classList.remove('spa-hidden');
      }
      element.setAttribute('aria-hidden', false);
    }
  },
  // "fn.maskingRules" methods - the class specific functions that hold the logic and data values specific to each unique masking rule
  maskingRules: {
    /* "maskingRules.basic" method
     * "basic" applies the basic masking rules to the current value.
     *
     * Parameters:
     *     sValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sValue" string, with the basic mask applied
     */
    basic(sValue) {
      const sInputPattern = '^.+$'; // matches all characters in sValue
      const sMaskingPattern = 'allChars';
      return spartaMaskingDependencyUtility.fn.helpers.applyMask(sValue, sInputPattern, sMaskingPattern);
    },
    /* "maskingRules.email" method
     * "email" applies the email-specific masking rules to the current value.
     *
     * Parameters:
     *     sEmailValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sEmailValue" string, with the "email" mask applied
     */
    email(sEmailValue) {
      // the email mask is applied only to the "local" portion of the email address in sEmailValue
      const sMaskedPortion = sEmailValue.split('@')[0];
      let sMaskedValue = sMaskedPortion;
      const sMaskedPortionLength = sMaskedPortion.length;

      // if the local length is less than 3, mask all characters
      if (sMaskedPortionLength < 3) {
        sMaskedValue = spartaMaskingDependencyUtility.fn.maskingRules.basic(sMaskedPortion);
      } else {
        // if the local length is greater than 3, mask all characters except the first and last ones
        const sSubstringPortion = sMaskedPortion.substring(1, sMaskedPortionLength - 1);
        const sMaskedSubstringValue = spartaMaskingDependencyUtility.fn.maskingRules.basic(sSubstringPortion);
        sMaskedValue = sMaskedPortion.charAt(0) + sMaskedSubstringValue + sMaskedPortion.charAt(sMaskedPortionLength - 1);
      }
      return sEmailValue.replace(sMaskedPortion, sMaskedValue);
    },
    /* "maskingRules.phone" method
     * "phone" applies the phone-specific masking rules to the current value.
     *
     * Parameters:
     *     sPhoneValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sPhoneValue" string, with the "phone" mask applied
     */
    phone(sPhoneValue) {
      const sInputPattern = '^.+(?=.{4}$)'; // matches all but the last 4 characters in sPhoneValue
      const sMaskingPattern = 'alphaNumeric';
      return spartaMaskingDependencyUtility.fn.helpers.applyMask(sPhoneValue, sInputPattern, sMaskingPattern);
    },
    /* "maskingRules.account" method
     * "account" applies the account-specific masking rules to the current value.
     *
     * Parameters:
     *     sAccountValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sAccountValue" string, with the "account" mask
     *     applied
     */
    account(sAccountValue) {
      const sInputPattern = '^.+(?=.{4}$)'; // matches all but the last 4 characters in sAccountValue
      const sMaskingPattern = 'allChars';
      return spartaMaskingDependencyUtility.fn.helpers.applyMask(sAccountValue, sInputPattern, sMaskingPattern);
    },
    /* "maskingRules.date" method
     * "date" applies the date-specific masking rules to the current value.
     *
     * Parameters:
     *     sDateValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sDateValue" string, with the "date" mask applied
     */
    date(sDateValue) {
      const sInputPattern = '^.+$'; // matches all characters in sDateValue
      const sMaskingPattern = 'alphaNumeric';
      return spartaMaskingDependencyUtility.fn.helpers.applyMask(sDateValue, sInputPattern, sMaskingPattern);
    },
    /* "maskingRules.ssn" method
     * "ssn" applies the social security number/employer identification number-specific masking
     * rules to the current value.
     *
     * Parameters:
     *     sSSNValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sSSNValue" string, with the "ssn" mask applied
     */
    ssn(sSSNValue) {
      const sInputPattern = '^.+$'; // matches all characters in sSSNValue
      const sMaskingPattern = 'alphaNumeric';
      return spartaMaskingDependencyUtility.fn.helpers.applyMask(sSSNValue, sInputPattern, sMaskingPattern);
    }
  }
};

export { spartaMaskingDependencyUtility as default };
